import { Box, Button, CircularProgress, Dialog, Divider, IconButton, SxProps, Typography, useMediaQuery } from "@mui/material"
import { pxToRem, pxTovW } from "libs/shared-ui/src/commonUtils/resizeUtils";
import { IStyles } from "libs/shared-ui/src/commonUtils/styleUtils";
import { theme } from "libs/shared-ui/src/theme/themeProvider";
import ImageWrapper from "../../elements/ImageWrapper";
import { IconWrapper } from "../../elements/IconWrapper/Index";
import { StudentAssessmentTask } from "@protos/learning_management/lms.assessment.student.apis_pb";
import { TaskStudentAttemptStatusEnum } from "@protos/learning_management/lms.db_pb";
import { Timestamp } from "@bufbuild/protobuf";
// Uncomment when the project icon is added to the bucket.
// import { getMediaBasePath } from "libs/shared-ui/src/commonUtils/images";
import { remainingTimeInHoursAndMins } from "libs/shared-ui/src/commonUtils/homework";
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { MouseEvent, useState } from "react";

const GROUP_DETAILS_GREEN = '#0AA34F';
const GROUP_DETAILS_NAVY = '#475D99';

const styles: IStyles = {
    root: {
        height: { xs: pxToRem(158), md: pxTovW(290), lg: pxTovW(210) },
        width: { xs: pxToRem(340), md: pxTovW(590), lg: pxTovW(433) },
        borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
        backgroundColor: '#FFFFFF',
        border: '1px solid #475D99',
        padding: {
            xs: pxToRem(8),
            md: `${pxTovW(18)} ${pxTovW(15)}`,
            lg: `${pxTovW(13)} ${pxTovW(12)}`,
        },
        display: 'flex',
        flexDirection: 'column',
        // alignItems: 'stretch',
        gap: { xs: pxToRem(4), md: pxTovW(2), lg: pxTovW(3) },
        boxSizing: 'border-box',
        boxShadow: `0px 0px ${pxToRem(11)} #E7E7E7D9`,
        marginX: 'auto',
        cursor: 'pointer',
    },
    imageBox: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        height: { xs: '100%', md: '100%', lg: pxTovW(111) },
        width: { xs: pxToRem(103), md: pxTovW(221), lg: pxTovW(110) },
        minWidth: { xs: pxToRem(103), md: pxTovW(221), lg: pxTovW(111) },
        boxSizing: 'border-box',
        borderRadius: { xs: pxToRem(16), md: pxTovW(16) },
        backgroundColor: '#E0DFDE',
        position: 'relative',
    },
    image: {
        width: '100%',
        objectFit: 'cover',
        borderRadius: { xs: pxToRem(16), md: pxTovW(16) },
    },
    iconBox: {
        display: 'flex',
        gap: { xs: pxToRem(6), md: pxTovW(8) },
        alignItems: 'center',
        width: 'max-content',
    },

    BadgeContainer: {
        boxSizing: 'border-box',
        position: 'absolute',
        width: '100%',
        height: { xs: pxToRem(39), md: pxTovW(53) },
        top: { xs: pxToRem(16), md: pxTovW(20) },
    },

    popoverBox: {
        '& .MuiPopover-root': {},
        '& .MuiPopover-paper': {
            // width: { xs: pxTovW(277), md: pxTovW(183), lg: pxTovW(183) },
            borderRadius: { xs: pxToRem(10), md: pxToRem(10) },
        },
    },

    // ----- Group details popup (same look as the teacher's ProjectInfoCard) -----
    // md (iPad) uses rem: vw units based on the 1920 desktop design shrink to unreadable sizes there.
    gdPaper: {
        borderRadius: { xs: pxToRem(14), md: pxToRem(18), lg: pxTovW(20) },
        width: { xs: pxToRem(330), md: pxToRem(480), lg: pxTovW(560) },
        maxWidth: '92vw',
        backgroundColor: '#FFFFFF',
        boxShadow: `0px ${pxToRem(10)} ${pxToRem(40)} rgba(0, 0, 0, 0.18)`,
        overflow: 'hidden',
    },
    gdHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
        padding: {
            xs: `${pxToRem(14)} ${pxToRem(16)}`,
            md: `${pxToRem(16)} ${pxToRem(22)}`,
            lg: `${pxTovW(16)} ${pxTovW(24)}`,
        },
        borderBottom: '1px solid #E8E8E8',
    },
    gdTitle: {
        fontWeight: 700,
        color: '#1D1D1D',
        fontSize: { xs: pxToRem(16), md: pxToRem(19), lg: pxTovW(20) },
    },
    gdClose: {
        color: '#6B6B6B',
        padding: { xs: pxToRem(4), md: pxToRem(6), lg: pxTovW(4) },
    },
    gdContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: {
            xs: `${pxToRem(16)} ${pxToRem(16)}`,
            md: `${pxToRem(20)} ${pxToRem(22)}`,
            lg: `${pxTovW(20)} ${pxTovW(24)}`,
        },
        gap: { xs: pxToRem(12), md: pxToRem(14), lg: pxTovW(14) },
        maxHeight: { xs: '60vh', md: '62vh' },
        overflowY: 'auto',
    },
    gdRow: {
        display: 'flex',
        alignItems: 'center',
        gap: { xs: pxToRem(12), md: pxToRem(14), lg: pxTovW(14) },
    },
    gdIcon: {
        flexShrink: 0,
        color: GROUP_DETAILS_NAVY,
        fontSize: { xs: pxToRem(24), md: pxToRem(28), lg: pxTovW(26) },
    },
    gdPersonIcon: {
        flexShrink: 0,
        color: GROUP_DETAILS_GREEN,
        fontSize: { xs: pxToRem(24), md: pxToRem(28), lg: pxTovW(26) },
    },
    gdLabel: {
        color: '#1D1D1D',
        fontWeight: 700,
        lineHeight: 1.3,
        fontSize: { xs: pxToRem(15), md: pxToRem(17), lg: pxTovW(16) },
    },
    gdValue: {
        color: '#6B6B6B',
        fontWeight: 400,
        lineHeight: 1.3,
        fontSize: { xs: pxToRem(13), md: pxToRem(15), lg: pxTovW(14) },
    },
    gdDivider: {
        borderColor: '#E8E8E8',
    },
    gdLoader: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: { xs: pxToRem(24), md: pxToRem(28), lg: pxTovW(28) },
    },
};
interface IProps {
    project?: StudentAssessmentTask;
    onClick?: () => void;
    rootStyles?: SxProps;
    isEnded?: boolean;
    /** Name shown against "Created By"; falls back to the project's teacher name. */
    createdBy?: string;
    /** The student's collaboration group for this project. Left undefined until loaded (see `onDetailsOpen`). */
    groupName?: string;
    /** Show a loader inside the popup while `groupName` is being fetched. */
    groupDetailsLoading?: boolean;
    /** Fired when the details popup is opened - use it to lazy-load `groupName`. */
    onDetailsOpen?: () => void;
}
export const StudentProjectCard = (props: IProps) => {
    const {
        project,
        onClick,
        rootStyles,
        isEnded,
        createdBy,
        groupName,
        groupDetailsLoading,
        onDetailsOpen,
    } = props;

    let rootSx = { ...styles.root };
    if (rootStyles) rootSx = { ...rootSx, ...rootStyles };
    const [detailsOpen, setDetailsOpen] = useState(false);

    const handleDetailsClick = (ev: MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        setDetailsOpen(true);
        onDetailsOpen?.();
    };
    const handleDetailsClose = () => setDetailsOpen(false);

    // Submitted but not yet ended: still listed as active, shown as done.
    const isCompleted =
        !isEnded &&
        project?.studentAttemptStatus ===
            TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED;

    const resolvedCreatedBy = createdBy || project?.teacherName || '-';
    const resolvedGroupName = groupName?.trim() || '-';
    // A project can span chapters; fall back to the module when no chapter info is set.
    const resolvedChapterName =
        (project?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo ?? [])
            .map((chapter) => chapter.name?.trim())
            .filter(Boolean)
            .join(', ') ||
        project?.moduleName?.trim() ||
        '-';
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
    const isIpadOnly = !isMobile && isIpad;
    const formatAssessmentDate = (timestamp: Timestamp | undefined): string => {
        if (!timestamp) return '-';

        const date = new Date(Number(timestamp.seconds) * 1000);

        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
        }).replace(/ /g, '-');
    };

    const formatAssessmentDeadlineDate = (timestamp: Timestamp | undefined): string => {
        if (!timestamp) return '-';

        const date = new Date(Number(timestamp.seconds) * 1000);

        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }).replace(/ /g, '-');
    };

    return (
        <>
        <Box
            sx={rootSx}
            onClick={onClick}
        >
            <Box sx={{
                display: 'flex',
                gap: { xs: pxToRem(15), md: pxTovW(15), lg: pxTovW(23) },
                boxSizing: 'border-box',
                width: '100%'
            }}>
                <Box sx={styles.imageBox}>
                    <ImageWrapper
                        name="create_project_icon"
                        type="png"
                        parentFolder="icons"
                        styles={{
                            ...styles.image,
                            height: '100%',
                        }}
                        // Uncomment when the project icon is added to the bucket.
                        // path={getMediaBasePath(
                        //     project?.assessmentPosterImgUrl,
                        //     'processedMediaBucket'
                        // )}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: { xs: pxToRem(3), md: pxTovW(4) },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            // alignItems: 'center',
                            justifyContent: 'space-between',
                            height: { xs: pxToRem(17), md: pxTovW(28) },
                            gap: { xs: pxToRem(10), md: pxTovW(16) },
                            marginTop: { xs: pxToRem(2), md: pxTovW(10), lg: pxTovW(5) },
                            marginBottom: { xs: pxToRem(1), md: pxTovW(5) },
                        }}
                    >

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: 'max-content',
                                background: '#475D99',
                                padding: {
                                    xs: `${pxToRem(3)} ${pxToRem(7)} ${pxToRem(4)}`,
                                    md: `${pxTovW(4)} ${pxTovW(14)} ${pxTovW(7)}`,
                                    lg: `${pxTovW(5)} ${pxTovW(14)} ${pxTovW(7)}`,
                                },
                                borderRadius: {
                                    xs: pxToRem(10),
                                    md: pxTovW(20),
                                    lg: pxTovW(15),
                                },
                            }}
                        >
                            <Typography
                                variant={isMobile ? 'smallestText' : 'subText'}
                                color="common.white"
                                fontWeight={700}
                            >
                                Project
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: pxToRem(5), md: pxTovW(5) },
                                mt: { xs: pxToRem(5), md: pxTovW(5) },
                            }}
                        >
                            <Button
                                onClick={handleDetailsClick}
                                sx={{ padding: 0, minWidth: 'unset' }}
                            >
                                <IconWrapper
                                    name="tooltip-black"
                                    parentFolder="icons"
                                    type="png"
                                    size="small"
                                    customSx={{
                                        height: {
                                            xs: pxToRem(14),
                                            md: pxTovW(20),
                                            lg: pxTovW(14),
                                        },
                                        width: {
                                            xs: pxToRem(14),
                                            md: pxTovW(20),
                                            lg: pxTovW(14),
                                        },
                                        cursor: 'pointer',
                                    }}
                                />
                            </Button>
                        </Box>

                    </Box>
                    <Typography
                        variant={isMobile ? 'smallestText' : 'subText'}
                        fontWeight={700}
                        color="primary.main"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordWrap: 'break-word',
                        }}
                    >
                        {project?.subject}
                    </Typography>
                    <Typography
                        variant={'h4'}
                        fontWeight={600}
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordWrap: 'break-word',
                            maxWidth: {
                                xs: pxToRem(190),
                                md: pxTovW(320),
                                lg: pxTovW(220),
                            },
                        }}
                    >
                        {project?.assessmentTitle}
                    </Typography>
                    <Typography
                        variant={isMobile ? 'smallestText' : 'subText'}
                        fontWeight={500}
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordWrap: 'break-word',
                            maxWidth: {
                                xs: pxToRem(190),
                                md: pxTovW(320),
                                lg: pxTovW(220),
                            },
                            // marginTop: { xs: pxToRem(4), md: pxTovW(2) },
                        }}
                    >
                        {project?.moduleName}
                    </Typography>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box sx={styles.iconBox}>
                                <IconWrapper
                                    name="individual"
                                    size={isIpadOnly ? 'md' : 'small'}
                                    parentFolder="icons"
                                    type="png"
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography variant="subText" fontWeight={'bold'}>
                                        Group
                                    </Typography>
                                    <Typography variant="smallestText" fontWeight="regular" color={'#B3B3B3'}>
                                        Type
                                    </Typography>
                                </Box>

                            </Box>
                            <Box sx={styles.iconBox}>
                                {!isEnded && !isCompleted &&
                                    <IconWrapper
                                        name="project_calendar"
                                        size={isIpadOnly ? 'md' : 'small'}
                                        parentFolder="icons"
                                        type="png"
                                    />
                                }
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {isEnded || isCompleted ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                background: '#E1FFEE',
                                                border: '1px solid #0AA34F',
                                                padding: {
                                                    xs: `${pxToRem(4)} ${pxToRem(6)}`,
                                                    md: `${pxTovW(4)} ${pxTovW(7)} ${pxTovW(7)}`,
                                                },
                                                borderRadius: {
                                                    xs: pxToRem(10),
                                                    md: pxTovW(21),
                                                    lg: pxTovW(16),
                                                },
                                            }}
                                        >

                                            <Typography variant="smallText" color={'secondary.main'}>
                                                Submitted
                                            </Typography>
                                        </Box>
                                        // <Typography
                                        //     variant="subText" fontWeight={'bold'}
                                        // >
                                        //     Submitted
                                        // </Typography>
                                    ) : (
                                        <>
                                            <Typography
                                                variant="subText" fontWeight={'bold'}
                                            >
                                                {formatAssessmentDeadlineDate(project?.endDate)}

                                            </Typography>
                                            <Typography variant="smallestText" fontWeight="regular" color={'#B3B3B3'}>
                                                DeadLine
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    width: "100%",
                    py: 1,
                }}
            >
                <Divider
                    sx={{
                        width: "100%",
                        borderColor: "#E0E0E0",
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: { xs: pxToRem(8), md: pxTovW(10) },
                }}
            >
                <Typography
                    variant={'subText'}
                    fontWeight={700}
                    color={"#81858C"}
                >
                    {project?.startDate &&
                    Number(project.startDate.seconds) * 1000 > Date.now()
                        ? `Starts on ${formatAssessmentDate(project.startDate)}`
                        : `Assigned on ${formatAssessmentDate(project?.startDate)}`}
                </Typography>
                {!isEnded && (
                    <Box sx={styles.iconBox}>
                        <IconWrapper
                            name="clock"
                            size={isIpadOnly ? 'md' : 'small'}
                            parentFolder="icons"
                            type="png"
                        />
                        <Typography
                            variant={'subText'}
                            fontWeight={700}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {remainingTimeInHoursAndMins(project?.endDate)}
                        </Typography>
                        <Typography
                            variant={'subText'}
                            fontWeight={400}
                            color={"#81858C"}
                        >
                            Remaining
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>

            <Dialog
                open={detailsOpen}
                onClose={handleDetailsClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{ sx: styles.gdPaper }}
            >
                <Box sx={styles.gdHeader}>
                    <Typography sx={styles.gdTitle}>Group Details</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleDetailsClose}
                        sx={styles.gdClose}
                    >
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={styles.gdContent}>
                    {/* Chapter Name - comes with the task, so it never waits on the group fetch */}
                    <Box sx={styles.gdRow}>
                        <MenuBookRoundedIcon sx={styles.gdIcon} />
                        <Box>
                            <Typography sx={styles.gdLabel}>Chapter Name</Typography>
                            <Typography sx={styles.gdValue}>
                                {resolvedChapterName}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={styles.gdDivider} />

                    {groupDetailsLoading ? (
                        <Box sx={styles.gdLoader}>
                            <CircularProgress
                                size={22}
                                sx={{ color: GROUP_DETAILS_GREEN }}
                            />
                        </Box>
                    ) : (
                        <>
                            {/* Group Name */}
                            <Box sx={styles.gdRow}>
                                <GroupsRoundedIcon sx={styles.gdIcon} />
                                <Box>
                                    <Typography sx={styles.gdLabel}>Group Name</Typography>
                                    <Typography sx={styles.gdValue}>
                                        {resolvedGroupName}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={styles.gdDivider} />

                            {/* Created By */}
                            <Box sx={styles.gdRow}>
                                <PersonOutlineRoundedIcon sx={styles.gdPersonIcon} />
                                <Box>
                                    <Typography sx={styles.gdLabel}>Created By</Typography>
                                    <Typography sx={styles.gdValue}>
                                        {resolvedCreatedBy}
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Dialog>
        </>
    )
}

export default StudentProjectCard
